package com.fireavert.react.shared.components.nav

import com.fireavert.react.shared.styles.MobileStyles.MOBILE_VIEW_WIDTH
import com.fireavert.react.shared.styles.ModernNavStyles
import com.fireavert.react.shared.styles.NavStyles
import com.fireavert.react.shared.styles.NavStylesV2
import com.fireavert.topnav.client.presentation.TopNavController
import com.fireavert.topnav.client.presentation.TopNavViewModel
import com.fireavert.utilities.getKoinInstance
import emotion.css.cx
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import mui.base.ClickAwayListener
import mui.material.*
import mui.system.useMediaQuery
import react.*
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.img
import web.dom.Element

external interface MobileTopNavBarProps : Props {
    var firstNameProp: String
    var lastNameProp: String
    var rolesProp: String
    var initialsProp: String
    var drawerOpenProp: Boolean
    var toggleDrawer: () -> Unit
    var controllerProp: TopNavController
    var activeNav: NavOption
    var isLoadingProp: Boolean
}

external interface DesktopTopNavBarProps : Props {
    var firstNameProp: String
    var lastNameProp: String
    var rolesProp: String
    var popoverOpenProp: Boolean
    var setPopoverOpen: (Boolean) -> Unit
    var savedAnchorElProp: Element?
    var setSavedAnchorEl: (Element?) -> Unit
    var controllerProp: TopNavController
    var isLoadingProp: Boolean
}

external interface TopNavBarV2Props : Props {
    var activeNavOption: NavOption
}

val TopNavBarV2 = FC<TopNavBarV2Props> { props ->
    val controller = getKoinInstance<TopNavController>()
    val viewModel = getKoinInstance<TopNavViewModel>()
    val isMobile = useMediaQuery(MOBILE_VIEW_WIDTH)
    var firstName by useState("")
    var lastName by useState("")
    var roles by useState("")
    var popoverOpen by useState(false)
    var drawerOpen by useState(false)
    var savedAnchorEl: Element? by useState(null)
    var initials by useState("")
    // Add a loading state to track when data is ready
    var isLoading by useState(true)

    var viewStateJob: Job?

    useEffectWithCleanup {
        // First set up the flow collection
        viewStateJob = viewModel.viewState.onEach { viewState ->
            firstName = viewState.firstName
            lastName = viewState.lastName
            roles = viewState.roles

            // Safely calculate initials
            initials = if (firstName.isNotEmpty() && lastName.isNotEmpty()) {
                "${firstName.first()}${lastName.first()}"
            } else if (firstName.isNotEmpty()) {
                "${firstName.first()}"
            } else if (lastName.isNotEmpty()) {
                "${lastName.first()}"
            } else {
                ""
            }

            // Mark as no longer loading once we have data
            isLoading = false
        }.launchIn(viewModel.scope)

        // Then trigger data loading
        controller.onLoad()

        onCleanup {
            viewStateJob?.cancel()
        }
    }

    fun toggleDrawer() {
        drawerOpen = !drawerOpen
    }

    if (isMobile) {
        Fragment { // Use Fragment to return multiple top-level elements for the mobile case
            MobileTopNavBar { // This will now just be the bar itself
                firstNameProp = firstName
                lastNameProp = lastName
                rolesProp = roles
                initialsProp = initials
                // drawerOpenProp = drawerOpen // No longer needed by MobileTopNavBar directly for MUI Drawer
                toggleDrawer = ::toggleDrawer // Hamburger will call this
                // activeNav = props.activeNavOption // Pass to MobileNavV2 instead
                controllerProp = controller
                isLoadingProp = isLoading
            }

            // Mobile Navigation Overlay (controlled by TopNavBarV2's drawerOpen state)
            div {
                className = cx(
                    ModernNavStyles.mobileNavOverlay,
                    if (drawerOpen) ModernNavStyles.mobileNavOverlayOpen else null
                )
                onClick = { drawerOpen = false } // Clicking overlay closes nav
            }

            // Our Custom Mobile Navigation Drawer
            // (controlled by TopNavBarV2's drawerOpen state)
            MobileNavV2 {
                isOpen = drawerOpen
                onClose = { drawerOpen = false } // Pass the function to close it
                activeNavOption = props.activeNavOption
            }
        }
    } else {
        DesktopTopNavBar {
            firstNameProp = firstName
            lastNameProp = lastName
            rolesProp = roles
            popoverOpenProp = popoverOpen
            setPopoverOpen = { popoverOpen = it }
            savedAnchorElProp = savedAnchorEl
            setSavedAnchorEl = { savedAnchorEl = it }
            controllerProp = controller
            isLoadingProp = isLoading
        }
    }
}

val MobileTopNavBar = FC<MobileTopNavBarProps> { props ->
    var popoverOpen by useState(false)
    var savedAnchorEl: Element? by useState(null)

    div {
        ariaLabel = "TopNav"
        className = NavStylesV2.topNav

        div {
            ClickAwayListener {
                onClickAway = { if (props.drawerOpenProp) props.toggleDrawer() }

                Fragment {
                    div {
                        className = NavStylesV2.notificationBell
                        IconButton {
                            onClick = { props.toggleDrawer() }
                            img {
                                src = "/static/hamburger_menu.svg"
                                alt = "Menu"
                            }
                            color = IconButtonColor.primary
                        }
                    }

                    Drawer {
                        anchor = DrawerAnchor.left
                        open = props.drawerOpenProp
                        onClose = { _, _ -> props.toggleDrawer() }

                        MobileNavV2 { activeNavOption = props.activeNav }
                    }
                }
            }
        }
        div {
            className = NavStylesV2.mobileLogo
            a {
                href = "/app/dashboard"
                img {
                    src = "/static/white_logo.svg"
                }
            }
        }
        div {
            ariaLabel = "User Menu"
            onClick = {
                // Only allow popover to open if data is loaded
                if (!props.isLoadingProp) {
                    popoverOpen = true
                }
            }
            className = NavStylesV2.userMenu
            div {
                className = NavStyles.userMenuName
                // Show either initials or a loading indicator
                if (props.isLoadingProp) {
                    +"..." // Simple loading indicator, you could use a spinner here
                } else {
                    +props.initialsProp
                }
            }
            div {
                ref = RefCallback {
                    savedAnchorEl = it
                }
                className = NavStyles.userMenuArrow
            }
        }
        Popover {
            open = popoverOpen
            onClose = { _, _ ->
                popoverOpen = false
            }
            anchorReference = PopoverReference.anchorEl
            anchorEl = savedAnchorEl

            Paper {
                MenuItem {
                    onClick = {
                        props.controllerProp.clickedProfile()
                        popoverOpen = false
                    }
                    +"My Profile"
                }
                MenuItem {
                    onClick = {
                        props.controllerProp.clickedLogout()
                        popoverOpen = false
                    }
                    +"Logout"
                }
            }
        }
    }
}

// Update DesktopTopNavBar to handle loading state
val DesktopTopNavBar = FC<DesktopTopNavBarProps> { props ->
    div {
        ariaLabel = "TopNav"
        className = NavStylesV2.topNav

        div {
            ariaLabel = "User Menu"
            onClick = {
                // Only allow popover to open if data is loaded
                if (!props.isLoadingProp) {
                    props.setPopoverOpen(true)
                }
            }
            className = NavStylesV2.userMenu
            div {
                className = NavStyles.userMenuName
                // Show user info or loading indicator
                if (props.isLoadingProp) {
                    +"Loading..." // Simple loading indicator
                } else {
                    +"${props.firstNameProp} ${props.lastNameProp} ${props.rolesProp}"
                }
            }
            div {
                ref = RefCallback {
                    props.setSavedAnchorEl(it)
                }
                className = NavStyles.userMenuArrow
            }
        }
        Popover {
            open = props.popoverOpenProp
            onClose = { _, _ ->
                props.setPopoverOpen(false)
            }
            anchorReference = PopoverReference.anchorEl
            anchorEl = props.savedAnchorElProp

            Paper {
                MenuItem {
                    onClick = {
                        props.controllerProp.clickedProfile()
                        props.setPopoverOpen(false)
                    }
                    +"My Profile"
                }
                MenuItem {
                    onClick = {
                        props.controllerProp.clickedLogout()
                        props.setPopoverOpen(false)
                    }
                    +"Logout"
                }
            }
        }
    }
}
